import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageLoading } from '../common/LoadingStates';

const OneTimeLogin = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, validateAndSetUser } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Verifying login link...');
  const attemptRef = useRef(false);

  useEffect(() => {
    const handleOneTimeLogin = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid login link - no token found');
        return;
      }

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
        const response = await fetch(`${API_BASE_URL}/auth/one-time-login?token=${token}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Login failed');
        }

        const data = await response.json();
        
        if (data.access_token) {
          // Store the token in localStorage and update context
          await validateAndSetUser(data.access_token);
          
          // Decode JWT to get user info for navigation decision
          const payload = JSON.parse(atob(data.access_token.split('.')[1]));
          
          setStatus('success');
          setMessage('Login successful! Redirecting...');
          
          // Redirect based on role (similar to LiffLogin pattern)
          setTimeout(() => {
            if (payload.role === 'admin') {
              navigate('/admin/dashboard');
            } else {
              navigate('/line/dashboard');
            }
          }, 1500);
        } else {
          throw new Error('No access token received');
        }
      } catch (error) {
        console.error('One-time login error:', error);
        setStatus('error');
        setMessage(error.message || 'Login failed. Please try again.');
      }
    };

    if (!attemptRef.current) {
      attemptRef.current = true;
      handleOneTimeLogin();
    }
  }, [searchParams, navigate, login, validateAndSetUser]);

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-center">
            <PageLoading />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Verifying Login</h2>
          <p className="text-gray-600">{message}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-6 text-center bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-green-900">Login Successful!</h2>
          <p className="text-green-700">{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6 text-center bg-white rounded-xl shadow-lg p-8">
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-red-900">Login Failed</h2>
        <p className="text-red-700">{message}</p>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-colors duration-200"
          >
            Go to Login Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default OneTimeLogin;
